import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {
  FeatureFlagEnum,
  ModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SidebarLinkDocumentManager} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {DocumentsBrowserShell} from 'src/app/documents/DocumentsBrowserShell';
import {toDocumentTreeNodeId} from 'src/app/documents/documentTreeBrowserState';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useDocumentReviewsFeature} from 'src/shared/hooks/useDocumentReviewsFeature';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {documentTitle} from '../documentHelpers';
import {CurrentDocumentListView} from './components/CurrentDocumentListView';
import {DocumentAcknowledgementsTab} from './components/DocumentAcknowledgementsTab';
import {DocumentApprovalsTab} from './components/DocumentApprovalsTab';

type DocumentListTabView = 'current' | 'approvals' | 'acknowledgements';

const DocumentListPage: React.FC = () => {
  useSetDocumentTitle(documentTitle);
  const hasReviewsFeature = useDocumentReviewsFeature();

  // Tab selection lives in a query param (not the `:nodeId` path segment,
  // which is reserved for folder navigation within the Current View tab) so
  // folder deep-links keep working unchanged regardless of the active tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const {nodeId} = useParams();
  const selectedTreeId = toDocumentTreeNodeId(nodeId);

  const {RenderedTabsComponent} = useUrlQueryTabV2<DocumentListTabView>({
    view: (searchParams.get('tab') ?? 'current') as DocumentListTabView,
    defaultView: 'current',
    tabs: [
      {
        view: 'current',
        label: 'Documents',
        component: <CurrentDocumentListView />,
      },
      {
        view: 'approvals',
        label: 'Approvals',
        component: <DocumentApprovalsTab />,
      },
      {
        view: 'acknowledgements',
        label: 'Acknowledgements',
        component: <DocumentAcknowledgementsTab />,
      },
    ],
    onSelectView: view => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set('tab', view);
          return next;
        },
        {replace: true}
      );
    },
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.DocumentManagement}>
      <DocumentsBrowserShell selectedId={selectedTreeId}>
        {hasReviewsFeature ? (
          <>
            <PageHeader>
              <PageHeader.Title icon={SidebarLinkDocumentManager.icon}>
                {documentTitle}
              </PageHeader.Title>
            </PageHeader>
            {RenderedTabsComponent}
          </>
        ) : (
          <CurrentDocumentListView />
        )}
      </DocumentsBrowserShell>
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.DocumentsManagerList(':nodeId?'),
  element: <DocumentListPage />,
  module: ModuleEnum.Documents,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});

export default DocumentListPage;
